import { ref } from 'vue';

import { defineStore } from 'pinia';

import { $axiosManager } from '@/api/$axios.js';

export const useInvitesStore = defineStore('invites', () => {
  const invitesData = ref(null);

  const getInvites = async (params) => {
    const res = await $axiosManager.get('/invites', { params });
    // Python returns { items: [...] }; interceptor auto-converts fields to camelCase
    invitesData.value = res.data.items ?? [];
    return invitesData.value;
  };

  const createInvite = async (params) => {
    // interceptor auto-converts: { userRole } → { user_role }; response is 201 with
    // { emailFailed } when the invite was saved but the email could not be sent
    const res = await $axiosManager.post('/invites', params);
    return res?.data ?? null;
  };

  const acceptInvite = async (params) => {
    // Callers pass { code }; identity comes from the bearer token (backend-owned).
    await $axiosManager.put('/invites/accept', params);
  };

  const rejectInvite = async (params) => {
    // Callers pass { code, email }; public soft status update (no login required).
    await $axiosManager.patch('/invites/reject', params);
  };

  const resendInvite = async (params) => {
    // ID moves from query param to path
    await $axiosManager.put(`/invites/${params.id}/resend`);
  };

  const changeRole = async (params) => {
    return $axiosManager.put('/business-profiles/users/role', params);
  };

  const setUserAccess = async ({ sub, enabled }) => {
    return $axiosManager.put('/business-profiles/users/access', { sub, enabled });
  };

  const cancelInvite = async (id) => {
    // ID moves from query param to path; response is 204 (no body)
    await $axiosManager.delete(`/invites/${id}`);
  };

  // Verify an invitation code before the user is authenticated (registration page).
  //   GET /manager/api/invites/status?code=<code>   (public, no auth required)
  //     200 → { active: true }   — live, unused, non-expired invite
  //     200 → { active: false }  — unknown / expired / already-used code
  // The `/invites` prefix is allow-listed in gateGuard.js and $axiosManager GETs
  // work pre-auth (same as /referral-program).
  const validateInviteCode = async (code) => {
    const trimmed = String(code ?? '').trim();
    if (!trimmed) return { active: false };

    const res = await $axiosManager.get('/invites/status', { params: { code: trimmed } });
    return { active: !!res.data?.active };
  };

  return {
    getInvites,
    createInvite,
    resendInvite,
    acceptInvite,
    rejectInvite,
    cancelInvite,
    validateInviteCode,
    changeRole,
    setUserAccess,
    invitesData,
  };
});
